// ./ProductRecommendations.js

import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import QuickAccessToolbar from '../../components/ui/QuickAccessToolbar';
import SectionContextMenu from '../../components/ui/SectionContextMenu';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import RecommendationCard from './components/RecommendationCard';
import FilterSidebar from './components/FilterSidebar';
import CategoryTabs from './components/CategoryTabs';
import ActiveFilters from './components/ActiveFilters';
import ProductDetailModal from './components/ProductDetailModal';
import { mockProducts } from '../../data/recprods.js';

const ProductRecommendations = () => {
  const [activeCategory, setActiveCategory] = useState('all');
  const [showFilters, setShowFilters] = useState(false);
  const [viewMode, setViewMode] = useState('grid'); // grid or list
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showProductModal, setShowProductModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState({
    priceRange: [0, 200],
    skinConcerns: [],
    ingredients: [],
    brands: [],
    sortBy: 'compatibility',
    sampleAvailable: false,
    onSale: false,
    crueltyFree: false,
    fragranceFree: false
  });

  const categories = [
    { id: 'all', label: 'All Recommendations', count: mockProducts?.length },
    { id: 'missing-essentials', label: 'Missing Essentials', count: mockProducts?.filter(p => p?.category === 'Missing Essentials')?.length },
    { id: 'upgrade-options', label: 'Upgrade Options', count: mockProducts?.filter(p => p?.category === 'Upgrade Options')?.length },
    { id: 'concern-specific', label: 'Concern-Specific', count: mockProducts?.filter(p => p?.category === 'Concern-Specific')?.length },
    { id: 'budget-alternatives', label: 'Budget Alternatives', count: mockProducts?.filter(p => p?.category === 'Budget Alternatives')?.length }
  ];

  const filteredProducts = mockProducts?.filter(product => {
    // Category filter
    if (activeCategory !== 'all') {
      const categoryMap = {
        'missing-essentials': 'Missing Essentials',
        'upgrade-options': 'Upgrade Options',
        'concern-specific': 'Concern-Specific',
        'budget-alternatives': 'Budget Alternatives'
      };
      if (product?.category !== categoryMap?.[activeCategory]) return false;
    }

    // Search filter
    if (searchQuery) {
      const query = searchQuery?.toLowerCase();
      if (!product?.name?.toLowerCase()?.includes(query) &&
          !product?.brand?.toLowerCase()?.includes(query) &&
          !product?.keyIngredients?.some(ing => ing?.toLowerCase()?.includes(query))) {
        return false;
      }
    }

    // Price range filter
    if (product?.price < filters?.priceRange?.[0] || product?.price > filters?.priceRange?.[1]) {
      return false;
    }

    // Brands filter
    if (filters.brands.length > 0 && !filters.brands.includes(product.brand)) {
        return false;
    }

    // Ingredients filter
    if (filters.ingredients.length > 0) {
        if (!product.keyIngredients || !filters.ingredients.some(filterIng => product.keyIngredients.includes(filterIng))) {
            return false;
        }
    }
    
    // Skin Concerns filter
    if (filters.skinConcerns.length > 0) {
        if (!product.skinConcerns || !filters.skinConcerns.some(concern => product.skinConcerns.includes(concern))) {
            return false;
        }
    }

    // Boolean filters
    if (filters.crueltyFree && !product.crueltyFree) return false;
    if (filters.fragranceFree && !product.fragranceFree) return false;
    if (filters.sampleAvailable && !product.sampleAvailable) return false;
    if (filters.onSale && (!product.originalPrice || product.originalPrice <= product.price)) return false;

    return true;
  });

  const sortedProducts = [...filteredProducts]?.sort((a, b) => {
    switch (filters?.sortBy) {
      case 'compatibility':
        return b?.compatibilityScore - a?.compatibilityScore;
      case 'rating':
        return b?.rating - a?.rating;
      case 'price-low':
        return a?.price - b?.price;
      case 'price-high':
        return b?.price - a?.price;
      case 'newest':
        return b?.id - a?.id;
      case 'popular':
        return b?.reviewCount - a?.reviewCount;
      default:
        return 0;
    }
  });

  const handleAddToRoutine = (product) => {
    console.log('Adding to routine:', product);
    // Implement add to routine logic
  };

  const handleSaveToWishlist = (productId, isWishlisted) => {
    console.log('Wishlist toggle:', productId, isWishlisted);
    // Implement wishlist logic
  };

  const handleViewDetails = (product) => {
    setSelectedProduct(product);
    setShowProductModal(true);
  };

  const handleFiltersChange = (newFilters) => {
    setFilters(newFilters);
  };

  const handleRemoveFilter = (filterType, filterValue) => {
    const newFilters = { ...filters };
    
    if (filterType === 'priceRange') {
      newFilters.priceRange = [0, 200];
    } else if (Array.isArray(newFilters?.[filterType])) {
      newFilters[filterType] = newFilters?.[filterType]?.filter(item => item !== filterValue);
    } else {
      newFilters[filterType] = false;
    }
    
    setFilters(newFilters);
  };

  const handleClearAllFilters = () => {
    setFilters({
      priceRange: [0, 200],
      skinConcerns: [],
      ingredients: [],
      brands: [],
      sortBy: 'compatibility',
      sampleAvailable: false,
      onSale: false,
      crueltyFree: false,
      fragranceFree: false
    });
  };

  return (
    <>
      <Helmet>
        <title>Product Recommendations - SkinScore Analyzer</title>
        <meta name="description" content="Discover personalized skincare product recommendations based on your routine analysis and skin assessment. Find the perfect products for your skin type and concerns." />
      </Helmet>
      <div className="min-h-screen bg-background">
        <QuickAccessToolbar />

        {/* Page Header */}
        <div className="bg-background border-b border-border">
          <div className="max-w-7xl mx-auto px-6 py-8">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="font-heading font-heading-bold text-3xl text-foreground mb-2">
                  Product Recommendations
                </h1>
                <p className="font-body font-body-normal text-muted-foreground">
                  Personalized product suggestions based on your skin analysis and routine compatibility
                </p>
              </div>
              
              {/* Desktop Toggles/Menu (Hidden on Mobile) */}
              <div className="hidden lg:flex items-center space-x-3"> 
                <SectionContextMenu />
                
                {/* View Mode Toggle (Desktop) */}
                <div className="flex items-center space-x-1 bg-muted rounded-clinical p-1">
                  <Button
                    variant={viewMode === 'grid' ? 'default' : 'ghost'}
                    size="sm"
                    onClick={() => setViewMode('grid')}
                    iconName="Grid3X3"
                    iconSize={16}
                    className="h-8 w-8 p-0"
                  >
                    <span className="sr-only">Grid view</span>
                  </Button>
                  <Button
                    variant={viewMode === 'list' ? 'default' : 'ghost'}
                    size="sm"
                    onClick={() => setViewMode('list')}
                    iconName="List"
                    iconSize={16}
                    className="h-8 w-8 p-0"
                  >
                    <span className="sr-only">List view</span>
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Mobile Toggles Bar (Visible only on Mobile) */}
        <div className="lg:hidden bg-background border-b border-border">
          <div className="max-w-7xl mx-auto px-6 py-3 flex justify-center items-center space-x-3">
            
            {/* View Mode Toggle (Mobile) */}
            <div className="flex items-center space-x-1 bg-muted rounded-clinical p-1">
              {/* <Button
                variant={viewMode === 'grid' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('grid')}
                iconName="Grid3X3"
                iconSize={16}
                className="h-8 w-8 p-0"
              >
                <span className="sr-only">Grid view</span>
              </Button>
              <Button
                variant={viewMode === 'list' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('list')}
                iconName="List"
                iconSize={16}
                className="h-8 w-8 p-0"
              >
                <span className="sr-only">List view</span>
              </Button> */}
            </div>

            {/* Filter Toggle (Mobile - now correctly positioned) */}
            <Button
              variant="outline"
              size="xl"
              onClick={() => setShowFilters(!showFilters)}
              iconName="Filter"
              iconPosition="left"
              iconSize={16}
              className="border-2 border-primary text-primary hover:bg-primary/10 transition-colors font-body-medium text-base" 
/* Added: border-2 (thicker border), border-primary, text-primary, font-body-medium, text-base */
            >
              Filters
            </Button>
          </div>
        </div>

        {/* Category Tabs */}
        <CategoryTabs
          categories={categories}
          activeCategory={activeCategory}
          onCategoryChange={setActiveCategory}
        />
{/* --- */}
        {/* Active Filters */}
        <ActiveFilters
          filters={filters}
          onRemoveFilter={handleRemoveFilter}
          onClearAll={handleClearAllFilters}
        />

{/* --- */}
        {/* Main Content */}
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="flex gap-6">
            {/* Desktop Filter Sidebar */}
            <div className={`hidden lg:block w-80 flex-shrink-0 ${showFilters ? 'block' : 'hidden'}`}>
              <div className="sticky top-32">
                <FilterSidebar
                  filters={filters}
                  onFiltersChange={handleFiltersChange}
                  onClearFilters={handleClearAllFilters}
                  products={mockProducts}
                />
              </div>
            </div>

            {/* Mobile Filter Overlay */}
            {showFilters && (
              <div className="lg:hidden fixed inset-0 z-secondary-nav bg-background/80 backdrop-blur-sm">
                <div className="absolute inset-y-0 left-0 w-80 bg-background border-r border-border">
                  <div className="flex items-center justify-between p-4 border-b border-border">
                    <h2 className="font-heading font-heading-semibold text-lg text-foreground">
                      Filters
                    </h2>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => setShowFilters(false)}
                      iconName="X"
                      iconSize={20}
                    >
                      <span className="sr-only">Close filters</span>
                    </Button>
                  </div>
                  <FilterSidebar
                    filters={filters}
                    onFiltersChange={handleFiltersChange}
                    onClearFilters={handleClearAllFilters}
                    products={mockProducts}
                    className="h-full"
                  />
                </div>
              </div>
            )}

            {/* Products Grid */}
            <div className="flex-1">
              {/* Search and Results Count */}
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center space-x-4">
                  <div className="relative">
                    <Input
                      type="search"
                      placeholder="Search products, brands, ingredients..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e?.target?.value)}
                      className="w-80 pl-10"
                    />
                    <Icon
                      name="Search"
                      size={16}
                      className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground pointer-events-none"
                    />
                  </div>
                </div>

                <div className="font-body font-body-normal text-sm text-muted-foreground">
                  {sortedProducts?.length} products found
                </div>
              </div>

              {/* Products */}
              {sortedProducts?.length > 0 ? (
                <div className={`grid gap-6 ${
                  viewMode === 'grid' ?'grid-cols-1 md:grid-cols-2 xl:grid-cols-3' :'grid-cols-1'
                }`}>
                  {sortedProducts?.map((product) => (
                    <RecommendationCard
                      key={product?.id}
                      product={product}
                      onAddToRoutine={handleAddToRoutine}
                      onSaveToWishlist={handleSaveToWishlist}
                      onViewDetails={handleViewDetails}
                    />
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <Icon name="Search" size={48} className="text-muted-foreground mx-auto mb-4" />
                  <h3 className="font-heading font-heading-medium text-lg text-foreground mb-2">
                    No products found
                  </h3>
                  <p className="font-body font-body-normal text-muted-foreground mb-4">
                    Try adjusting your filters or search terms to find more products.
                  </p>
                  <Button
                    variant="outline"
                    onClick={handleClearAllFilters}
                    iconName="RotateCcw"
                    iconPosition="left"
                    iconSize={16}
                  >
                    Clear All Filters
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Product Detail Modal */}
        <ProductDetailModal
          product={selectedProduct}
          isOpen={showProductModal}
          onClose={() => setShowProductModal(false)}
          onAddToRoutine={handleAddToRoutine}
          onSaveToWishlist={handleSaveToWishlist}
        />
      </div>
    </>
  );
};

export default ProductRecommendations;